// src/services/consentService.js
// Consent management client. API calls go through the shared apiClient, which
// attaches the bearer token and recovers from a stale/revoked session on 401.
// Direct Supabase table reads (getBusinessesForConsent) stay on the supabase
// client.
import { supabase } from '../lib/supabase';
import { apiFetch } from './apiClient';

export const consentService = {
  // ============================================================================
  // CONSENT MANAGEMENT
  // ============================================================================

  /**
   * Get all consents for the user (as grantor or grantee)
   * @param {string} role - 'grantor' (consents you've given) or 'grantee' (consents given to you) or null for both
   * @param {boolean} activeOnly - Only return active (non-revoked, non-expired) consents
   */
  async getConsents(role = 'grantor', activeOnly = true) {
    try {
      const params = new URLSearchParams();
      if (role) params.append('role', role);
      if (activeOnly) params.append('active_only', 'true');

      const data = await apiFetch(`/v1/consents?${params}`);
      return { data: data.consents, error: null };
    } catch (error) {
      console.error('Error fetching consents:', error);
      return { data: null, error };
    }
  },

  /**
   * Get a specific consent by ID
   */
  async getConsent(consentId) {
    try {
      const data = await apiFetch(`/v1/consents/${consentId}`);
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching consent:', error);
      return { data: null, error };
    }
  },

  /**
   * Create a new consent grant
   * @param {string} granteeId - User ID to grant access to (typically a business)
   * @param {string[]} scopes - Array of permission scopes (e.g., ['availability:read:freebusy', 'booking:propose'])
   * @param {Date} expiresAt - Optional expiration date
   * @param {Object} timeRange - Optional time range restriction { from, to }
   */
  async createConsent(granteeId, scopes, expiresAt = null, timeRange = null) {
    try {
      const body = {
        grantee_id: granteeId,
        scopes
      };

      if (expiresAt) {
        body.expires_at = expiresAt.toISOString();
      }

      if (timeRange) {
        body.time_range = timeRange;
      }

      const data = await apiFetch('/v1/consents', { method: 'POST', body });
      return { data, error: null };
    } catch (error) {
      console.error('Error creating consent:', error);
      return { data: null, error };
    }
  },

  /**
   * Revoke a consent (soft delete)
   */
  async revokeConsent(consentId) {
    try {
      await apiFetch(`/v1/consents/${consentId}`, { method: 'DELETE' });
      return { data: true, error: null };
    } catch (error) {
      console.error('Error revoking consent:', error);
      return { data: null, error };
    }
  },

  /**
   * Get business profiles with their user IDs (for consent granting)
   */
  async getBusinessesForConsent() {
    try {
      const { data, error } = await supabase
        .from('business_profiles')
        .select(`
          business_name,
          service_type,
          location,
          user_id,
          user:user_id (
            id,
            email,
            full_name
          )
        `)
        .order('business_name');

      if (error) throw error;

      // Filter out businesses that don't have a valid user profile
      // This prevents "Grantee not found" errors
      const validBusinesses = (data || []).filter(business => {
        if (!business.user || !business.user.id) {
          console.warn(`Skipping business "${business.business_name}" - no valid user profile for user_id: ${business.user_id}`);
          return false;
        }
        return true;
      });

      return { data: validBusinesses, error: null };
    } catch (error) {
      console.error('Error fetching businesses:', error);
      return { data: null, error };
    }
  }
};
